import React from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { Column } from "@webbio/components";

import { RunEventDTO, RunEventDTOActionEnum } from "@app/api/generated";
import { Table } from "@app/components/table/table";
import { ROUTES } from "@app/constants/routes";

import styles from "@app/styles/dashboard/events-table.module.scss";

import { EVENT_ACTION_LABEL_KEY } from "../event-actions";

interface IEventsTableProps {
  events: RunEventDTO[];
  isLoading: boolean;
}

/** Actions whose product is gone (DELETE) or never existed (ERROR) → no link. */
const NON_LINKABLE_ACTIONS = new Set<RunEventDTOActionEnum>([
  RunEventDTOActionEnum.DELETE,
  RunEventDTOActionEnum.ERROR
]);

/** Per-action badge accent (base badge = grey). */
const ACTION_BADGE_CLASS: Partial<Record<RunEventDTOActionEnum, string>> = {
  [RunEventDTOActionEnum.SET_OFFLINE]: styles.badgeYellow,
  [RunEventDTOActionEnum.DELETE]: styles.badgeYellow,
  [RunEventDTOActionEnum.ERROR]: styles.badgeRed,
  [RunEventDTOActionEnum.CREATE]: styles.badgeBlue
};

const EventsTable = ({ events, isLoading }: IEventsTableProps): JSX.Element => {
  const intl = useIntl();

  return (
    <Table dataSource={events} isLoading={isLoading} noHover>
      <Column
        field="action"
        title={intl.formatMessage({ id: "productPageSync.events.table.action" })}
        maxWidth={140}
        width={140}
        cell={(action: RunEventDTOActionEnum) => (
          <span className={clsx(styles.badge, ACTION_BADGE_CLASS[action])}>
            {intl.formatMessage({ id: EVENT_ACTION_LABEL_KEY[action] })}
          </span>
        )}
      />
      <Column
        field="productId"
        title={intl.formatMessage({ id: "productPageSync.events.table.productId" })}
        maxWidth={64}
        width={64}
        cell={(productId: number | null, _index: number, event: RunEventDTO) =>
          productId != null && !NON_LINKABLE_ACTIONS.has(event.action) ? (
            <Link to={`${ROUTES.PRODUCTS}/${productId}`} className={styles.productLink}>
              {productId}
            </Link>
          ) : (
            <>{productId ?? "—"}</>
          )
        }
      />
      <Column
        field="ean"
        title={intl.formatMessage({ id: "productPageSync.events.table.ean" })}
        cell={(ean: string | null) => (
          <span className="truncate-table" title={ean ?? "-"}>
            {ean ?? "—"}
          </span>
        )}
        minWidth={120}
      />
      <Column
        field="slug"
        title={intl.formatMessage({ id: "productPageSync.events.table.slug" })}
        cell={(slug: string | null) => (
          <span className="truncate-table" title={slug ?? "-"}>
            {slug ?? "—"}
          </span>
        )}
      />
      <Column
        field="message"
        title={intl.formatMessage({ id: "productPageSync.events.table.message" })}
        cell={(message: string | null) => (
          <span className="truncate-table" title={message ?? "-"}>
            {message ?? "—"}
          </span>
        )}
      />
    </Table>
  );
};

export { EventsTable };
